import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    useReactFlow,
    Node,
    Edge,
    ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Maximize2, Minimize2, RefreshCw, Server, Cloud, AlertTriangle, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

import { apiClient, InventoryCluster, FlowSummary, AWSConnection, OCIConnection, CloudResource, CloudIncidentSummary } from '../../api/client';
import { CustomNodeData, EnrichedFlowSummary, ClusterCacheEntry } from './types';
import {
    ContainerNode,
    ClusterNode,
    NamespaceNode,
    NodeNode,
    WorkloadNode,
    ServiceNode,
    SummaryNode,
    ExternalNamespaceNode,
    ExternalIpNode,
    KnownIpNode,
    AwsAccountNode,
    AwsVpcNode,
    AwsGlobalGroupNode,
    AwsResourceNode,
    OciTenancyNode,
    OciVcnNode,
    OciGlobalGroupNode,
    OciResourceNode,
} from './NodeComponents';
import { ClickableFlowEdge, FlowDetailsDialog } from './FlowComponents';
import { ManageKnownIPsDialog } from './ManageKnownIPsDialog';

import { useTopologyCache } from './hooks/useTopologyCache';
import { useLayoutEngine } from './hooks/useLayoutEngine';
import { useExpansionState } from './hooks/useExpansionState';
import { useFlowEdges, FlowEdgeData } from './hooks/useFlowEdges';

// ============================================================================
// NODE TYPES
// ============================================================================

const nodeTypes = {
    container: ContainerNode,
    cluster: ClusterNode,
    namespace: NamespaceNode,
    node: NodeNode,
    workload: WorkloadNode,
    service: ServiceNode,
    summary: SummaryNode,
    'external-namespace': ExternalNamespaceNode,
    'external-ip': ExternalIpNode,
    'known-ip': KnownIpNode,
    // AWS Cloud Resource nodes
    'aws-account': AwsAccountNode,
    'aws-vpc': AwsVpcNode,
    'aws-global-group': AwsGlobalGroupNode,
    'aws-resource': AwsResourceNode,
    // OCI Cloud Resource nodes
    'oci-tenancy': OciTenancyNode,
    'oci-vcn': OciVcnNode,
    'oci-global-group': OciGlobalGroupNode,
    'oci-resource': OciResourceNode,
};

const edgeTypes = {
    'clickable-flow': ClickableFlowEdge,
};

// ============================================================================
// TOOLBAR COMPONENT
// ============================================================================

const Toolbar: React.FC<{
    onCollapseAll: () => void;
    onExpandAll: () => Promise<void>;
    onRefresh: () => void;
    onKnownIPsClick: () => void;
    isLoading: boolean;
    expandedCount: number;
    totalClusters: number;
}> = ({ onCollapseAll, onExpandAll, onRefresh, onKnownIPsClick, isLoading, expandedCount, totalClusters }) => {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <Button
                variant="outline"
                size="sm"
                onClick={onCollapseAll}
                disabled={expandedCount === 0}
            >
                <Minimize2 className="h-4 w-4 mr-1" />
                Collapse All
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={onExpandAll}
                disabled={expandedCount === totalClusters || isLoading}
            >
                <Maximize2 className="h-4 w-4 mr-1" />
                Expand All
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={isLoading}
            >
                <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={onKnownIPsClick}
                className="border-zinc-300 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900/30"
            >
                <Server className="h-4 w-4 mr-1" />
                Known IPs
            </Button>
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function TopologyMapExpansionInner() {
    // =========================================================================
    // STATE
    // =========================================================================

    const [clusters, setClusters] = useState<InventoryCluster[]>([]);
    const [awsConnections, setAwsConnections] = useState<AWSConnection[]>([]);
    const [ociConnections, setOciConnections] = useState<OCIConnection[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // React Flow state
    const [nodes, setNodes, onNodesChange] = useNodesState<Node<CustomNodeData>>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
    const [selectedFlow, setSelectedFlow] = useState<EnrichedFlowSummary | null>(null);
    const [showKnownIPsDialog, setShowKnownIPsDialog] = useState(false);
    const [selectedCloudResource, setSelectedCloudResource] = useState<CloudResource | null>(null);
    const [showCloudResourceDialog, setShowCloudResourceDialog] = useState(false);

    // React Flow instance for programmatic control
    const { fitView } = useReactFlow();

    // =========================================================================
    // HOOKS
    // =========================================================================

    // Data caching
    const {
        clusterCache,
        namespaceCache,
        awsAccountCache,
        ociTenancyCache,
        loadingNodes,
        loadClusterData,
        loadNamespaceData,
        loadAwsAccountData,
        loadOciTenancyData,
        clearCache,
    } = useTopologyCache();

    // Cloud resource click handler
    const handleCloudResourceClick = useCallback((resource: CloudResource) => {
        setSelectedCloudResource(resource);
        setShowCloudResourceDialog(true);
    }, []);

    // Expansion state management
    const {
        viewState,
        collapseAll,
        expandAllClusters,
        handleNodeClick,
    } = useExpansionState({
        clusters,
        clusterCache,
        loadClusterData,
        loadNamespaceData,
        awsConnections,
        awsAccountCache,
        loadAwsAccountData,
        ociConnections,
        ociTenancyCache,
        loadOciTenancyData,
        onCloudResourceClick: handleCloudResourceClick,
    });

    // Layout engine
    const { nodes: layoutNodes } = useLayoutEngine({
        clusters,
        viewState,
        clusterCache,
        namespaceCache,
        loadingNodes,
        awsConnections,
        awsAccountCache,
        ociConnections,
        ociTenancyCache,
    });

    // Get visible node IDs for flow edge generation
    const visibleNodeIds = useMemo(() => {
        return new Set(layoutNodes.map(n => n.id));
    }, [layoutNodes]);

    // Flow edges
    const flowEdges = useFlowEdges({
        viewState,
        clusterCache,
        namespaceCache,
        awsAccountCache,
        ociTenancyCache,
        visibleNodeIds,
    });

    // =========================================================================
    // LOAD INITIAL DATA (CLUSTERS, AWS CONNECTIONS, OCI CONNECTIONS)
    // =========================================================================

    useEffect(() => {
        const loadInitialData = async () => {
            setIsInitialLoading(true);
            setError(null);

            try {
                // Load clusters, AWS connections, and OCI connections in parallel
                const [clustersRes, awsRes, ociRes] = await Promise.all([
                    apiClient.getInventoryClusters(),
                    apiClient.getAWSConnectionStatus().catch(err => {
                        console.warn('[TopologyMap] AWS connections not available:', err);
                        return { connections: [] };
                    }),
                    apiClient.getOCIConnectionStatus().catch(err => {
                        console.warn('[TopologyMap] OCI connections not available:', err);
                        return { connections: [] };
                    })
                ]);

                setClusters(clustersRes.clusters || []);
                // Filter to only connected AWS accounts
                const connectedAwsAccounts = (awsRes.connections || []).filter(
                    c => c.connection_status === 'connected'
                );
                setAwsConnections(connectedAwsAccounts);
                // Filter to only connected OCI tenancies
                const connectedOciTenancies = (ociRes.connections || []).filter(
                    c => c.connection_status === 'connected'
                );
                setOciConnections(connectedOciTenancies);
            } catch (err: any) {
                console.error('[TopologyMap] Error loading initial data:', err);
                setError(err.message || 'Failed to load topology data');
            } finally {
                setIsInitialLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // =========================================================================
    // UPDATE REACT FLOW STATE WHEN LAYOUT CHANGES
    // =========================================================================

    // Transform edges to use clickable-flow type with click handler
    const transformedEdges = useMemo(() => {
        return flowEdges.map(edge => ({
            ...edge,
            type: 'clickable-flow',
            data: {
                ...edge.data,
                payload: edge.data, // Pass the original data as payload for the click handler
                onEdgeClick: (payload: FlowEdgeData) => {
                    // Create an EnrichedFlowSummary from the edge data for the dialog
                    const originalFlows = (payload.originalFlows as FlowSummary[]) || [];
                    const firstFlow = originalFlows[0];
                    const enrichedFlow: EnrichedFlowSummary = {
                        src_namespace: payload.srcNamespace,
                        src_name: payload.srcName,
                        src_kind: payload.srcKind || '',
                        dst_namespace: payload.dstNamespace,
                        dst_name: payload.dstName,
                        dst_kind: payload.dstKind || '',
                        protocol: payload.protocol,
                        port: payload.port,
                        allowed: payload.allowed,
                        suspicious: payload.suspicious,
                        count: payload.count,
                        first_seen: firstFlow?.first_seen || 0,
                        last_seen: firstFlow?.last_seen || Math.floor(Date.now() / 1000),
                        originalFlows,
                    };
                    setSelectedFlow(enrichedFlow);
                }
            }
        }));
    }, [flowEdges]);

    useEffect(() => {
        setNodes(layoutNodes);
        setEdges(transformedEdges);

        // Center view on the new layout after a short delay for DOM update
        const timer = setTimeout(() => {
            fitView({ padding: 0.2, duration: 300 });
        }, 50);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutNodes, transformedEdges]);

    // =========================================================================
    // HANDLERS
    // =========================================================================

    const handleRefresh = useCallback(async () => {
        // Save current expansion state before clearing cache
        const expandedClustersCopy = new Set(viewState.expandedClusters);
        const expandedNamespacesCopy = new Set(viewState.expandedNamespaces);
        const expandedAwsAccountsCopy = new Set(viewState.expandedAwsAccounts);
        const expandedOciTenanciesCopy = new Set(viewState.expandedOciTenancies);

        clearCache();
        setIsInitialLoading(true);
        setError(null);

        try {
            // Refresh clusters, AWS connections, and OCI connections in parallel
            const [clustersRes, awsRes, ociRes] = await Promise.all([
                apiClient.getInventoryClusters(),
                apiClient.getAWSConnectionStatus().catch(err => {
                    console.warn('[TopologyMap] AWS connections not available:', err);
                    return { connections: [] };
                }),
                apiClient.getOCIConnectionStatus().catch(err => {
                    console.warn('[TopologyMap] OCI connections not available:', err);
                    return { connections: [] };
                })
            ]);

            setClusters(clustersRes.clusters || []);
            const connectedAwsAccounts = (awsRes.connections || []).filter(
                c => c.connection_status === 'connected'
            );
            setAwsConnections(connectedAwsAccounts);
            const connectedOciTenancies = (ociRes.connections || []).filter(
                c => c.connection_status === 'connected'
            );
            setOciConnections(connectedOciTenancies);

            // Reload data for previously expanded clusters, AWS accounts, and OCI tenancies
            // This ensures flows with known IP enrichment are refreshed
            const newClusters = clustersRes.clusters || [];

            // Map cluster UID to loaded data for namespace lookup
            const loadedClusterData = new Map<string, ClusterCacheEntry>();

            const clusterPromises = Array.from(expandedClustersCopy).map(async clusterUID => {
                const cluster = newClusters.find(c => c.uid === clusterUID);
                if (cluster) {
                    const data = await loadClusterData(clusterUID, cluster);
                    loadedClusterData.set(clusterUID, data);
                }
            });

            const awsPromises = Array.from(expandedAwsAccountsCopy).map(async accountId => {
                const connection = connectedAwsAccounts.find(c => c.account_id === accountId);
                if (connection) {
                    await loadAwsAccountData(accountId, connection);
                }
            });

            const ociPromises = Array.from(expandedOciTenanciesCopy).map(async tenancyOcid => {
                const connection = connectedOciTenancies.find(c => c.tenancy_ocid === tenancyOcid);
                if (connection) {
                    await loadOciTenancyData(tenancyOcid, connection);
                }
            });

            // Wait for clusters/accounts/tenancies to reload first
            await Promise.all([...clusterPromises, ...awsPromises, ...ociPromises]);

            // Now reload any expanded namespaces using the loaded cluster data
            const namespaceReloadPromises = Array.from(expandedNamespacesCopy).map(async namespaceID => {
                // Find the namespace in the loaded cluster data
                for (const [clusterUID, clusterData] of Array.from(loadedClusterData.entries())) {
                    const namespace = clusterData?.namespaces?.find(ns => ns.id === namespaceID);
                    if (namespace) {
                        await loadNamespaceData(namespaceID, namespace, clusterUID);
                        break;
                    }
                }
            });
            await Promise.all(namespaceReloadPromises);
        } catch (err: any) {
            console.error('[TopologyMap] Error refreshing:', err);
            setError(err.message || 'Failed to refresh');
        } finally {
            setIsInitialLoading(false);
        }
    }, [clearCache, viewState, loadClusterData, loadNamespaceData, loadAwsAccountData, loadOciTenancyData]);

    const onNodeClickHandler = useCallback(
        async (event: React.MouseEvent, node: Node<CustomNodeData>) => {
            // Prevent click handling if loading
            if (loadingNodes.size > 0) return;
            await handleNodeClick(event, node);
        },
        [handleNodeClick, loadingNodes]
    );

    const closeFlowDialog = useCallback(() => {
        setSelectedFlow(null);
    }, []);

    // =========================================================================
    // RENDER
    // =========================================================================

    if (isInitialLoading) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <Card className="max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <p className="text-destructive font-medium">Error Loading Topology</p>
                            <p className="text-muted-foreground mt-2">{error}</p>
                            <Button onClick={handleRefresh} className="mt-4">
                                Try Again
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (clusters.length === 0 && awsConnections.length === 0 && ociConnections.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <Card className="max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <p className="text-lg font-medium">No Infrastructure Found</p>
                            <p className="text-muted-foreground mt-2">
                                Connect a Kubernetes cluster or AWS account to see the topology map.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex flex-col" style={{ height: 'calc(100 * var(--vh) - 140px)' }}>
            {/* Toolbar */}
            <div className="flex items-center justify-end px-4 py-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <Toolbar
                    onCollapseAll={collapseAll}
                    onExpandAll={expandAllClusters}
                    onRefresh={handleRefresh}
                    onKnownIPsClick={() => setShowKnownIPsDialog(true)}
                    isLoading={loadingNodes.size > 0}
                    expandedCount={viewState.expandedClusters.size + viewState.expandedAwsAccounts.size + viewState.expandedOciTenancies.size}
                    totalClusters={clusters.length + awsConnections.length + ociConnections.length}
                />
            </div>

            {/* React Flow Canvas */}
            <div className="flex-1 relative">
                <div className="absolute inset-0">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={onNodeClickHandler}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    fitView
                    fitViewOptions={{
                        padding: 0.2,
                        includeHiddenNodes: false,
                    }}
                    minZoom={0.1}
                    maxZoom={2}
                    defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                    proOptions={{ hideAttribution: true }}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={true}
                    edgesFocusable={true}
                    selectNodesOnDrag={false}
                >
                    <Background />
                    <Controls />
                </ReactFlow>
                </div>

                {/* Flow Details Dialog */}
                <FlowDetailsDialog
                    flow={selectedFlow}
                    onClose={closeFlowDialog}
                />

                {/* Manage Known IPs Dialog */}
                <ManageKnownIPsDialog
                    open={showKnownIPsDialog}
                    onClose={() => setShowKnownIPsDialog(false)}
                    onUpdate={handleRefresh}
                />

                {/* Cloud Resource Details Dialog */}
                <CloudResourceDetailsDialog
                    resource={selectedCloudResource}
                    open={showCloudResourceDialog}
                    onOpenChange={setShowCloudResourceDialog}
                />
            </div>
        </div>
    );
}

// ============================================================================
// CLOUD RESOURCE DETAILS DIALOG
// ============================================================================

interface CloudResourceDetailsDialogProps {
    resource: CloudResource | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function CloudResourceDetailsDialog({ resource, open, onOpenChange }: CloudResourceDetailsDialogProps) {
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState<CloudIncidentSummary[]>([]);
    const [isLoadingIncidents, setIsLoadingIncidents] = useState(false);
    const [incidentsError, setIncidentsError] = useState<string | null>(null);

    // Fetch cloud incidents for this resource when dialog opens
    useEffect(() => {
        if (!resource || !open) {
            setIncidents([]);
            setIncidentsError(null);
            return;
        }

        let isCancelled = false;

        const fetchIncidents = async () => {
            setIsLoadingIncidents(true);
            setIncidentsError(null);

            try {
                // Use the efficient backend endpoint that filters by resource
                const response = await apiClient.getCloudIncidentsByResource({
                    arn: resource.arn,
                    resourceId: resource.resource_id,
                    cloudProvider: resource.cloud_provider,
                    awsConnectionId: resource.aws_connection_id,
                    awsAccountId: resource.aws_account_id,
                    ociConnectionId: resource.oci_connection_id,
                    ociTenancyOcid: resource.oci_tenancy_ocid
                });

                if (!isCancelled) {
                    setIncidents(response.incidents || []);
                }
            } catch (error: any) {
                if (!isCancelled) {
                    console.error('Error fetching cloud incidents:', error);
                    setIncidentsError('Failed to load incidents');
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingIncidents(false);
                }
            }
        };

        fetchIncidents();

        return () => {
            isCancelled = true;
        };
    }, [resource, open]);

    if (!resource) return null;

    const renderDataPoint = (label: string, value: string | undefined | null) => {
        if (!value) return null;
        return (
            <div className="grid grid-cols-[200px,1fr] gap-x-4 py-3 border-b last:border-b-0">
                <span className="font-semibold text-sm text-muted-foreground">{label}</span>
                <span className="text-sm break-all font-mono">{value}</span>
            </div>
        );
    };

    const formatResourceType = (type: string) => {
        const parts = type.split('::');
        if (parts.length >= 3) {
            return `${parts[1]} ${parts[2]}`;
        }
        return type;
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical': return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300';
            case 'high': return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'low': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300';
            default: return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300';
        }
    };

    const getBorderColor = (severity: string) => {
        switch (severity) {
            case 'critical': return 'border-l-red-500';
            case 'high': return 'border-l-orange-500';
            case 'medium': return 'border-l-yellow-500';
            case 'low': return 'border-l-blue-500';
            default: return 'border-l-gray-500';
        }
    };

    // Format text that contains {{code}}...{{/code}} markers into actual code elements
    const formatCodeTags = (text: string) => {
        if (!text) return null;
        const parts = text.split(/(\{\{code\}\}.*?\{\{\/code\}\})/gi);
        return parts.map((part, index) => {
            const codeMatch = part.match(/\{\{code\}\}(.*?)\{\{\/code\}\}/i);
            if (codeMatch) {
                return (
                    <code key={index} className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono">
                        {codeMatch[1]}
                    </code>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    const renderIncidentsContent = () => {
        if (isLoadingIncidents) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            );
        }

        if (incidentsError) {
            return (
                <Alert variant="destructive">
                    <AlertDescription>{incidentsError}</AlertDescription>
                </Alert>
            );
        }

        if (!incidents || incidents.length === 0) {
            return (
                <div className="text-center py-12">
                    <ShieldCheck className="mx-auto h-12 w-12 text-green-600" />
                    <h3 className="mt-2 text-sm font-medium text-foreground">No incidents</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        No incidents have been detected for this resource.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-4 py-4">
                <div className="text-sm text-muted-foreground mb-4">
                    Found {incidents.length} incident{incidents.length !== 1 ? 's' : ''} affecting this resource (all time)
                </div>
                <div className="space-y-4">
                    {incidents.map((incident) => (
                        <Card key={incident.id} className={cn("border-l-4", getBorderColor(incident.severity))}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-destructive" />
                                            <h4 className="text-sm font-semibold leading-none tracking-tight">
                                                {incident.type}
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-2 pt-1">
                                            <Badge className={cn("text-xs", getSeverityColor(incident.severity))}>
                                                {incident.severity.toUpperCase()}
                                            </Badge>
                                            <Badge variant="outline" className="text-xs">
                                                {incident.status}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                {incident.rca_result?.root_cause && (
                                    <p className="text-sm text-muted-foreground mb-3">{formatCodeTags(incident.rca_result.root_cause)}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                    Detected: {new Date(incident.detected_at * 1000).toLocaleString()}
                                </p>
                                <div className="mt-3 pt-3 border-t">
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 h-auto text-xs"
                                        onClick={() => navigate(`/cloud-incidents/${incident.id}`)}
                                    >
                                        View Full Incident Details
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl h-[650px] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Cloud className="h-5 w-5" />
                        {formatResourceType(resource.resource_type)}: {resource.name || resource.resource_id}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-hidden">
                    <Tabs defaultValue="details" className="w-full h-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="details">Details</TabsTrigger>
                            <TabsTrigger value="incidents" className="flex items-center justify-center gap-2">
                                <span>Incidents</span>
                                {!isLoadingIncidents && incidents.length > 0 && (
                                    <Badge variant="destructive" className="h-5 px-1.5">
                                        {incidents.length}
                                    </Badge>
                                )}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="details" className="mt-0 h-[500px] overflow-y-auto">
                            <div className="space-y-4 py-4">
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg font-semibold">Resource Information</CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <div className="space-y-0">
                                            {renderDataPoint("Name", resource.name)}
                                            {renderDataPoint("Resource ID", resource.resource_id)}
                                            {renderDataPoint("Type", resource.resource_type)}
                                            {renderDataPoint("ARN", resource.arn)}
                                            {renderDataPoint("Region", resource.region)}
                                            {resource.aws_account_id && renderDataPoint("AWS Account", resource.aws_account_id)}
                                            {resource.oci_tenancy_ocid && renderDataPoint("OCI Tenancy", resource.oci_tenancy_ocid)}
                                            {renderDataPoint("Status", resource.resource_status)}
                                            {renderDataPoint("VPC/VCN", resource.vpc_id)}
                                            {renderDataPoint("Subnet", resource.subnet_id)}
                                            {renderDataPoint("Availability Zone", resource.availability_zone)}
                                            {renderDataPoint("Private IP", resource.private_ip)}
                                            {renderDataPoint("Public IP", resource.public_ip)}
                                            {renderDataPoint("First Seen", resource.first_seen_at && !isNaN(new Date(resource.first_seen_at).getTime()) ? new Date(resource.first_seen_at).toLocaleString() : undefined)}
                                            {renderDataPoint("Last Seen", resource.last_seen_at && !isNaN(new Date(resource.last_seen_at).getTime()) ? new Date(resource.last_seen_at).toLocaleString() : undefined)}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        <TabsContent value="incidents" className="mt-0 h-[500px] overflow-y-auto">
                            {renderIncidentsContent()}
                        </TabsContent>
                    </Tabs>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// WRAPPED EXPORT
// ============================================================================

export function TopologyMapExpansion() {
    return (
        <ReactFlowProvider>
            <TopologyMapExpansionInner />
        </ReactFlowProvider>
    );
}

export default TopologyMapExpansion;
